// 통합 일정 — 각 모듈 API 응답을 킷 달력(AXErpCalendar)이 그리는 한 모양(ScheduleItem)으로 바꾸는 순수 함수.
//
// 이 파일은 부수효과가 없고 다른 파일을 import 하지 않는다 — node --experimental-strip-types 로 시험된다(schedule.test.mjs).
// enum·namespace·파라미터 프로퍼티 금지.
//
// 원칙
//  · **본인 범위만** 만든다. 호출 쪽이 본인 사번으로만 조회한다(부서·전사 범위는 권한 정책이 정해진 뒤).
//  · 표시 문구(출처 이름·상태 이름)는 호출 쪽이 Labels 로 넘긴다 — 이 파일은 번역을 모른다. 제목에 들어가는 것은 DB 값(작업명·휴가명 등)이다.
//  · 날짜는 'YYYY-MM-DD', 시각 일정은 'YYYY-MM-DDTHH:mm' 문자열이다(Date·UTC 를 쓰지 않는다 — 킷 달력 계약).
//    종일은 마지막 날 포함, 시각은 끝 시각 제외.
//  · 서버가 모양을 바꿔도 화면이 죽지 않게 모르는 값은 건너뛴다(throw 하지 않는다).

export type ScheduleSource = 'pjt-task' | 'pjt-todo' | 'hr-leave' | 'ops-trip' | 'hr-attend';
export type ScheduleTone = 'accent' | 'ok' | 'warn' | 'danger' | 'violet' | 'gray';

export interface ScheduleItem {
  id: string;
  source: ScheduleSource;
  title: string;
  start: string;
  end?: string;
  allDay: boolean;
  tone: ScheduleTone;
  /** 장소·대상 — 프로젝트명·경로 등 */
  location?: string;
  /** 상태 이름(상세 패널에 보인다) */
  statusNm?: string;
  /** 원 모듈의 경로 접두어 — 눌러서 이동할 때 쓴다 */
  moduleCd: string;
}

/** 화면 문구 — 호출 쪽이 현재 언어로 만들어 넘긴다 */
export interface ScheduleLabels {
  leaveStatus: Record<number, string>;
  tripStatus: Record<number, string>;
  taskStatus: Record<number, string>;
  todoLate: string;
  attendAbsent: string;
  attendFixPending: string;
  attendReview: string;
  taskSub: string;
}

type Raw = Record<string, unknown> | null | undefined;

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;
const HHMM = /^([01]?\d|2[0-3]):?([0-5]\d)$/;

export function isIsoDay(v: unknown): v is string {
  return typeof v === 'string' && ISO_DAY.test(v);
}

function str(v: unknown): string {
  return typeof v === 'string' ? v.trim() : '';
}

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function rowsOf(body: unknown): Raw[] {
  if (!body || typeof body !== 'object') return [];
  const rows = (body as { rows?: unknown }).rows;
  return Array.isArray(rows) ? (rows as Raw[]) : [];
}

// ── 날짜 ──

/** 'YYYY-MM-DD' 에 일수를 더한다 — 로컬 시간대와 무관하게 UTC 날짜로만 계산한다(문자열 계약) */
export function addDays(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + n));
  return `${t.getUTCFullYear()}-${String(t.getUTCMonth() + 1).padStart(2, '0')}-${String(t.getUTCDate()).padStart(2, '0')}`;
}

/** 두 날짜가 겹치는가 — 둘 다 포함 구간 */
export function overlaps(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  return aFrom <= bTo && aTo >= bFrom;
}

/** 'HH:MM'(또는 'HHMM') → 'HH:mm'. 형식이 아니면 null */
export function normTime(v: unknown): string | null {
  const m = HHMM.exec(str(v));
  return m ? `${m[1].padStart(2, '0')}:${m[2]}` : null;
}

/** 'YYYY-MM-DDTHH:mm' 에 분을 더한다 — 같은 날을 넘으면 그날 23:59 로 막는다(끝 시각 제외 규칙에서 하루 안에 그린다) */
export function addMinutesSameDay(day: string, hhmm: string, minutes: number): string {
  const [h, mi] = hhmm.split(':').map(Number);
  const total = Math.min(h * 60 + mi + minutes, 24 * 60 - 1);
  return `${day}T${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

// ── 출처별 정규화 ──

/**
 * pjt 담당자 일정 — GET /api/pjt/schedule/load?empCds=본인 의 emps[0].tasks (작업 막대).
 * 기간(fromDa~toDa)이 있는 작업만 그린다. 완료는 회색, 지연은 빨강, 보류는 주황. 부담당(sub)은 이름 앞에 표시한다.
 */
export function pjtTaskItems(body: unknown, L: ScheduleLabels): ScheduleItem[] {
  const emps = body && typeof body === 'object' ? (body as { emps?: unknown }).emps : null;
  const first = Array.isArray(emps) ? (emps[0] as { tasks?: unknown } | undefined) : undefined;
  const tasks = first && Array.isArray(first.tasks) ? (first.tasks as Raw[]) : [];
  const out: ScheduleItem[] = [];
  for (const t of tasks) {
    const taskSq = num(t?.taskSq);
    const name = str(t?.taskNm);
    let from = t?.fromDa;
    let to = t?.toDa;
    if (taskSq === null || !name) continue;
    if (!isIsoDay(from) && !isIsoDay(to)) continue;
    if (!isIsoDay(from)) from = to;
    if (!isIsoDay(to)) to = from;
    const f = from as string;
    const e = to as string;
    const prog = num(t?.progTy);
    const late = t?.lateYn === true;
    const tone: ScheduleTone = prog === 3 ? 'gray' : late ? 'danger' : prog === 4 ? 'warn' : 'accent';
    const sub = str(t?.role) === 'sub';
    const pjtNm = str(t?.pjtNm);
    out.push({
      id: `pjt-task-${taskSq}`,
      source: 'pjt-task',
      title: sub ? `${L.taskSub} ${name}` : name,
      start: f <= e ? f : e,
      end: f <= e ? e : f,
      allDay: true,
      tone,
      location: pjtNm || undefined,
      statusNm: prog !== null ? L.taskStatus[prog] : undefined,
      moduleCd: 'pjt',
    });
  }
  return out;
}

/**
 * pjt 내 업무(개인 할 일) — GET /api/pjt/todos?progTy=open. 마감일(dueDa)이 있는 미완료만 그린다.
 */
export function pjtTodoItems(body: unknown, L: ScheduleLabels): ScheduleItem[] {
  const out: ScheduleItem[] = [];
  for (const r of rowsOf(body)) {
    const sq = num(r?.todoSq);
    const name = str(r?.todoNm);
    const due = r?.dueDa;
    if (sq === null || !name || !isIsoDay(due) || r?.useYn === false) continue;
    const prog = num(r?.progTy);
    if (prog === 3) continue;
    const late = r?.lateYn === true;
    out.push({
      id: `pjt-todo-${sq}`,
      source: 'pjt-todo',
      title: name,
      start: due,
      allDay: true,
      tone: late ? 'danger' : 'violet',
      statusNm: late ? L.todoLate : prog !== null ? L.taskStatus[prog] : undefined,
      moduleCd: 'pjt',
    });
  }
  return out;
}

/** hr 휴가 — GET /api/hr/leave-requests?empCd=본인&from&to. 신청(2)·승인(3)·변경요청(5)·취소요청(6)만 그린다. */
export function leaveItems(body: unknown, L: ScheduleLabels): ScheduleItem[] {
  const out: ScheduleItem[] = [];
  for (const r of rowsOf(body)) {
    const reqNo = num(r?.reqNo);
    const status = num(r?.statusTy);
    const from = r?.fromDa;
    const to = r?.toDa;
    if (reqNo === null || status === null || !isIsoDay(from)) continue;
    if (status !== 2 && status !== 3 && status !== 5 && status !== 6) continue;
    const e = isIsoDay(to) ? to : from;
    out.push({
      id: `hr-leave-${reqNo}`,
      source: 'hr-leave',
      title: str(r?.attyNm) || L.leaveStatus[status] || '',
      start: from <= e ? from : e,
      end: from <= e ? e : from,
      allDay: true,
      tone: status === 3 ? 'ok' : 'warn',
      statusNm: L.leaveStatus[status],
      moduleCd: 'hr',
    });
  }
  return out;
}

/**
 * ops 출장(항공권) — GET /api/ops/trips?empCd=본인&fromDa&toDa 의 rows[].legs[]. 취소(9)는 뺀다.
 * 출발 시각(depTm)이 있으면 한 시간짜리 시각 일정, 없으면 종일.
 */
export function tripItems(body: unknown, from: string, to: string, L: ScheduleLabels): ScheduleItem[] {
  const out: ScheduleItem[] = [];
  for (const trip of rowsOf(body)) {
    const tripSq = num(trip?.tripSq);
    const legs = trip && Array.isArray((trip as { legs?: unknown }).legs) ? ((trip as { legs: Raw[] }).legs) : [];
    if (tripSq === null) continue;
    for (const leg of legs) {
      const legSq = num(leg?.legSq);
      const day = leg?.depDa;
      const status = num(leg?.statusTy);
      if (legSq === null || !isIsoDay(day) || status === 9) continue;
      if (day < from || day > to) continue;
      const route = str(leg?.routeNm);
      const flight = str(leg?.flightNo);
      const tm = normTime(leg?.depTm);
      const title = [route, flight].filter(Boolean).join(' ') || str(trip?.rmkNm);
      out.push({
        id: `ops-trip-${tripSq}-${legSq}`,
        source: 'ops-trip',
        title,
        start: tm ? `${day}T${tm}` : day,
        end: tm ? addMinutesSameDay(day, tm, 60) : undefined,
        allDay: !tm,
        tone: status === 3 ? 'ok' : 'warn',
        statusNm: status !== null ? L.tripStatus[status] : undefined,
        moduleCd: 'ops',
      });
    }
  }
  return out;
}

/**
 * hr 근태 — GET /api/hr/attend-days?empCd=본인&from&to. 평소 근무일은 그리지 않고 **확인이 필요한 날만** 표시한다
 * (결근 > 정정 대기 > 확인 필요 순으로 하루 한 건).
 */
export function attendItems(body: unknown, L: ScheduleLabels): ScheduleItem[] {
  const out: ScheduleItem[] = [];
  for (const r of rowsOf(body)) {
    const day = r?.workDa;
    if (!isIsoDay(day)) continue;
    let title = '';
    let tone: ScheduleTone = 'warn';
    if (r?.absentYn === true) { title = L.attendAbsent; tone = 'danger'; }
    else if (r?.fixPendingYn === true) { title = L.attendFixPending; }
    else if (r?.reviewYn === true) { title = L.attendReview; }
    if (!title) continue;
    out.push({ id: `hr-attend-${day}`, source: 'hr-attend', title, start: day, allDay: true, tone, moduleCd: 'hr' });
  }
  return out;
}

/** 시작 → 제목 순으로 정렬한다(안정적인 목록·접근성 순서) */
export function sortItems(items: ScheduleItem[]): ScheduleItem[] {
  return items.slice().sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : a.title < b.title ? -1 : a.title > b.title ? 1 : 0));
}

/** 켜 둔 출처만 남긴다 */
export function filterBySource(items: ScheduleItem[], enabled: Record<string, boolean>): ScheduleItem[] {
  return items.filter(i => enabled[i.source] !== false);
}
